import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MyCollectionsTab } from '../components/MyCollectionsTab';
import { BrowseCollectionsTab } from '../components/BrowseCollectionsTab';
import { ProblemsTab } from './ProblemsTab';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useWarmup } from '../hooks/useWarmup';
import { AIPrepLogoIcon } from '../components/CustomIcons';
import '../styles/HomePage.css';

type TabType = 'my-collections' | 'browse' | 'problems' | 'reviews';

export const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Warm backend services when user lands on HomePage
  useWarmup(['collections', 'problems']);

  // Get initial tab from URL query parameter, or default based on auth status
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get('tab') as TabType | null;
    if (tabParam && ['my-collections', 'browse', 'problems', 'reviews'].includes(tabParam)) {
      return tabParam;
    }
    return isAuthenticated ? 'my-collections' : 'browse';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());

  // Update URL and page title when active tab changes
  useEffect(() => {
    // Update URL query parameter
    setSearchParams({ tab: activeTab }, { replace: true });

    // Update page title
    const titles: Record<TabType, string> = {
      'my-collections': 'My Collections - AIPrep',
      'browse': 'Popular Collections - AIPrep',
      'problems': 'Problems - AIPrep',
      'reviews': 'Reviews - AIPrep'
    };
    document.title = titles[activeTab];
  }, [activeTab, setSearchParams]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'my-collections':
        return <MyCollectionsTab onTabChange={setActiveTab} />;
      case 'browse':
        return <BrowseCollectionsTab />;
      case 'problems':
        return <ProblemsTab />;
      case 'reviews':
        return (
          <div className="reviews-coming-soon">
            <div className="coming-soon-icon">🧠</div>
            <h2>Daily Reviews</h2>
            <p className="coming-soon-subtitle">Coming Soon</p>
            <p className="coming-soon-description">
              Strengthen your understanding with spaced repetition. When you mark
              problems as completed, they'll be added to your review queue. Our AI
              will schedule reviews at optimal intervals to help you retain what
              you've learned.
            </p>
          </div>
        );
      default:
        return <MyCollectionsTab onTabChange={setActiveTab} />;
    }
  };

  return (
    <div className="home-page">
      <div className="tabs-container">
        <div className="tabs">
          {isAuthenticated ? (
            // When signed in: My Collections, Popular Collections, Problems
            <>
              <button
                className={`tab ${activeTab === 'my-collections' ? 'active' : ''}`}
                onClick={() => setActiveTab('my-collections')}
              >
                My Collections
              </button>
              <button
                className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
                onClick={() => setActiveTab('browse')}
              >
                Popular Collections
              </button>
            </>
          ) : (
            // When not signed in: Popular Collections, My Collections, Problems
            <>
              <button
                className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
                onClick={() => setActiveTab('browse')}
              >
                Popular Collections
              </button>
              <button
                className={`tab ${activeTab === 'my-collections' ? 'active' : ''}`}
                onClick={() => setActiveTab('my-collections')}
              >
                My Collections
              </button>
            </>
          )}
          <button
            className={`tab ${activeTab === 'problems' ? 'active' : ''}`}
            onClick={() => setActiveTab('problems')}
          >
            Problems
          </button>
          <button
            className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews
          </button>
        </div>
      </div>
      
      <div className="tab-content-container">
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>

      <footer className="home-footer">
        <div className="home-footer-content">
          <Link to="/" className="home-footer-logo">
            <span className="home-footer-logo-icon">
              <AIPrepLogoIcon size={22} color="white" />
            </span>
            AIPrep
          </Link>
          <div className="home-footer-links">
            <a href="mailto:support@aiprep.io">Feedback</a>
            <Link to="/privacy">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
